import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, AlertTriangle, Scale, DollarSign, RefreshCw } from 'lucide-react';

export default function Terms() {
  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Terms of Service & Disclaimer</h1>
        <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
      </div>

      <Card className="border-amber-500/50 bg-amber-500/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="w-5 h-5" />
            Important Disclaimer
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="font-semibold">
            Zero Deficit Sync is a financial tracking tool, NOT a substitute for professional tax or accounting advice.
          </p>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>We do not provide tax, legal, or financial advice</li>
            <li>Expense categorizations are automated and may require review</li>
            <li>Tax calculations are estimates only - consult a CPA for accuracy</li>
            <li>We are not liable for tax filing errors or missed deductions</li>
            <li>Always verify data before submitting to tax authorities</li>
          </ul>
          <p className="font-semibold text-amber-700 dark:text-amber-300">
            By using this app, you agree to consult with a qualified tax professional before making tax-related decisions.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[hsl(var(--accent))]" />
            Acceptance of Terms
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-3">
          <p>
            By accessing or using Zero Deficit Sync ("the App"), you agree to be bound by these Terms of Service
            ("Terms"). If you do not agree to these Terms, do not use the App.
          </p>
          <p>
            We reserve the right to modify these Terms at any time. Your continued use of the App after changes
            constitutes acceptance of the updated Terms.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-[hsl(var(--accent))]" />
            Service Description
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Zero Deficit Sync provides the following services:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>Expense and income tracking</li>
            <li>Bank account synchronization via Plaid</li>
            <li>Receipt scanning and OCR processing</li>
            <li>Mileage logging with distance calculation</li>
            <li>Tax form suggestions and write-off recommendations</li>
            <li>Data export and backup</li>
            <li>Push notifications for financial activity</li>
          </ul>
          <p className="text-sm font-semibold mt-3">
            These services are provided "AS IS" without warranties of any kind.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[hsl(var(--accent))]" />
            Subscription & Payments
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2 text-sm">Subscription Plans</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li><strong>Free Plan:</strong> Limited features (manual entry, basic tracking)</li>
              <li><strong>Pro Plan ($9.99/month):</strong> Unlimited transactions, Plaid sync, OCR, auto-match</li>
              <li><strong>Business Plan ($19.99/month):</strong> All Pro features + Schedule C generation, priority support</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold mb-2 text-sm">Billing Terms</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>Subscriptions are billed monthly or annually</li>
              <li>Automatic renewal unless canceled 24 hours before renewal date</li>
              <li>Payments processed via Apple App Store or Google Play Store</li>
              <li>No refunds for partial months - cancellation effective at end of billing period</li>
              <li>Prices subject to change with 30 days notice</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold mb-2 text-sm">Cancellation</h3>
            <p className="text-sm text-muted-foreground">
              You may cancel your subscription anytime via Settings → Subscription Management or through
              your App Store/Play Store account. No refunds for unused time.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-[hsl(var(--accent))]" />
            Restore Purchases
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <p>
            If you previously purchased a subscription and it's not showing, use the <strong>"Restore Purchases"</strong> button
            in Settings to restore your subscription status. This will verify your purchase with Apple/Google and reactivate your features.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>User Responsibilities</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
            <li>Maintain accurate account information and keep credentials secure</li>
            <li>Review all automated categorizations and data before relying on them</li>
            <li>Comply with all applicable tax laws in your jurisdiction</li>
            <li>Do not share your account or violate any laws using the App</li>
            <li>Backup your data regularly (we provide tools, but you're responsible)</li>
            <li>Report any bugs, security issues, or data discrepancies promptly</li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Limitation of Liability</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p className="font-semibold text-foreground">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>We are not liable for any tax penalties, interest, or fines</li>
            <li>We do not guarantee accuracy of OCR, categorizations, or calculations</li>
            <li>We are not responsible for Plaid, Google, or other third-party service failures</li>
            <li>We are not liable for data loss (though we provide backup tools)</li>
            <li>Our total liability is limited to the amount you paid in the last 12 months</li>
          </ul>
          <p className="font-semibold text-foreground mt-3">
            USE THIS APP AT YOUR OWN RISK. ALWAYS VERIFY DATA WITH A TAX PROFESSIONAL.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Intellectual Property</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <p>
            All content, features, and functionality of Zero Deficit Sync are owned by us and protected by
            copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute,
            or reverse-engineer any part of the App without written permission.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Termination</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            We may suspend or terminate your account if you violate these Terms, engage in fraudulent activity,
            or misuse the App. You may delete your account anytime via Settings → Delete Account.
          </p>
          <p className="font-semibold text-foreground">
            Upon termination, all your data will be permanently deleted within 30 days.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Governing Law</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <p>
            These Terms are governed by the laws of the State of California, USA. Any disputes shall be
            resolved through binding arbitration or in the courts of California.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Contact</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <p className="text-muted-foreground mb-2">Questions about these Terms?</p>
          <p><strong>Email:</strong> <a href="mailto:legal@zerodeficitflow.com" className="text-[hsl(var(--accent))] hover:underline">legal@zerodeficitflow.com</a></p>
          <p><strong>Support:</strong> <a href="mailto:support@zerodeficitflow.com" className="text-[hsl(var(--accent))] hover:underline">support@zerodeficitflow.com</a></p>
        </CardContent>
      </Card>

      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground">
            By using Zero Deficit Sync, you acknowledge that you have read, understood, and agree to be bound
            by these Terms of Service and our Privacy Policy.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
